import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, ChevronDown, MapPin, Truck, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";
import { useGetAllLocations } from "@/locations/api/queries/location.queries";
import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { useGetAllUsers } from "@/users/api/queries/user.queries";
import { TUser } from "@/users/types/user";

const SettingsResourceTree = () => {
  const { t } = useTranslation("settings");
  const { data: locationsData, isLoading: locationsLoading } = useGetAllLocations();
  const { data: usersData, isLoading: usersLoading } = useGetAllUsers();

  const [isLocationsExpanded, setIsLocationsExpanded] = useState(true);
  const [expandedLocations, setExpandedLocations] = useState<Set<number>>(new Set());

  const [isUsersExpanded, setIsUsersExpanded] = useState(true);

  const toggleLocation = (locationId: number) => {
    const newSet = new Set(expandedLocations);
    if (newSet.has(locationId)) {
      newSet.delete(locationId);
    } else {
      newSet.add(locationId);
    }
    setExpandedLocations(newSet);
  };

  if (locationsLoading && usersLoading) { // REVISAR
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  const locations = locationsData?.data || [];
  const users = usersData?.data || [];

  return (
    <div className="space-y-1">
      {/* Locations Header - Collapsible */}
      <button
        onClick={() => setIsLocationsExpanded(!isLocationsExpanded)}
        className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm hover:bg-accent/50 transition-colors text-foreground w-full font-medium"
      >
        {isLocationsExpanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <span className="flex-1 text-left">{t("resourceTree.locations")}</span>
      </button>

      {/* Locations List */}
      {isLocationsExpanded && (
        <div className="space-y-1">
          {locations.length > 0 ? (
            locations.map((location) => (
              <LocationTreeItem
                key={location.id}
                location={location}
                isExpanded={expandedLocations.has(location.id)}
                onToggle={() => toggleLocation(location.id)}
              />
            ))
          ) : (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t("resourceTree.noLocations")}</p>
          )}
        </div>
      )}

      {/* Users Header */}
      <button
        onClick={() => setIsUsersExpanded(!isUsersExpanded)}
        className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm hover:bg-accent/50 transition-colors text-foreground w-full font-medium"
      >
        {isUsersExpanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <span className="flex-1 text-left">{t("resourceTree.users")}</span>
      </button>

      {/* Users List */}
      {isUsersExpanded && (
        <div className="space-y-1">
          {users.length > 0 ? (
            users.map((user) => (
              <UserTreeItem key={user.username} user={user} />
            ))
          ) : (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t("resourceTree.noUsers")}</p>
          )}
        {/* Add new user */}
          <Link
            to="/settings/user/add"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm ml-6 text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground transition-colors"
          >
            <span className="text-base leading-none">+</span>
            <span>{t("resourceTree.addUser")}</span>
          </Link>
        </div>
      )}
    </div>
  );
};

interface LocationTreeItemProps {
  location: { id: number; name: string };
  isExpanded: boolean;
  onToggle: () => void;
}

const LocationTreeItem = ({ location, isExpanded, onToggle }: LocationTreeItemProps) => {
  const { t } = useTranslation("settings");
  const { data: machinesData, isLoading } = useGetMachinesByLocation(location.id);
  const locationInstance = useLocation();

  const machines = machinesData?.data || [];
  const isLocationActive = locationInstance.pathname === `/settings/location/${location.id}`;

  return (
    <div className="ml-2">
      {/* Location */}
      <div className="flex items-center">
        <button
          onClick={onToggle}
          className="p-1 hover:bg-accent/50 rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </button>
        <Link
          to={`/settings/location/${location.id}`}
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-md text-sm flex-1 transition-colors",
            isLocationActive
              ? "bg-accent text-accent-foreground font-medium"
              : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
          )}
        >
          <MapPin className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left truncate">{location.name}</span>
        </Link>
      </div>

      {/* Machines under this location */}
      {isExpanded && (
        <div className="ml-6 mt-1 space-y-1">
          {isLoading ? (
            <Skeleton className="h-8 w-full" />
          ) : machines.length > 0 ? (
            machines.map((machine) => {
              const isActive = locationInstance.pathname === `/settings/machine/${machine.id}`;
              return (
                <Link
                  key={machine.id}
                  to={`/settings/machine/${machine.id}`}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
                  )}
                >
                  <Truck className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{machine.name}</span>
                </Link>
              );
            })
          ) : (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t("resourceTree.noMachines")}</p>
          )}
        </div>
      )}
    </div>
  );
};

interface UserTreeItemProps {
  user: TUser;
}

const UserTreeItem = ({ user }: UserTreeItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === `/settings/user/${user.id}`;

  return (
    <Link
      to={`/settings/user/${user.id}`}
      state={{ user }}
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-md text-sm ml-6 transition-colors",
        isActive
          ? "bg-accent text-accent-foreground font-medium"
          : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
      )}
    >
      <User className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{user.username}</span>
    </Link>
  );
};

export default SettingsResourceTree;
